/**
 * Strongly Connected Components — Tarjan's iterative algorithm.
 *
 * Ported from `gantt/sources/core/relations/graph_helper.js`'s
 * `tarjanStronglyConnectedComponents()` and `findLoops()`. Iterative form
 * preserved (recursive Tarjan would blow the stack at 50k tasks per D-29).
 *
 * Public exports:
 *  - `tarjanSCC` — returns all strongly-connected components (including
 *    singletons).
 *  - `findCycles_unmemoized` — wraps SCC; returns cycles per `05-` §A.7
 *    `Cycle[]` shape: any SCC of size > 1, plus self-loop singletons.
 *    Matches v1 `findLoops()` semantics.
 *  - `findCycleWithCandidate` — pure helper behind
 *    `core/api.ts:findCycleIfAdded` (and thus `gantt.isCircularLink(link)`):
 *    checks whether adding the candidate's edges to an existing edge list
 *    would introduce a cycle, without mutating that list.
 *
 * Per D-15, cycle members are surfaced via `AnalysisResult.cycles` and
 * omitted from slack maps; the analysis pass uses these utilities.
 */

import type { NormalizedTaskID, NormalizedLinkID, Cycle, ConnectedGroup } from "../types";

interface Edge {
  id: NormalizedLinkID;
  source: NormalizedTaskID;
  target: NormalizedTaskID;
}

interface VertexRec {
  id: NormalizedTaskID;
  index: number;
  lowLink: number;
  onStack: boolean;
  visited: boolean;
  /** Edge taken to reach this vertex during DFS — used to attribute cycle to a link. */
  edge: Edge | undefined;
}

/**
 * Tarjan's iterative SCC. Returns every strongly-connected component in
 * the directed graph, including size-1 components (which v1's findLoops
 * filtered out unless they had a self-loop edge — that filter is in
 * `findCycles_unmemoized`).
 *
 * Each SCC includes the task IDs and the link IDs that closed the cycle.
 */
export function tarjanSCC(
  vertices: Iterable<NormalizedTaskID>,
  edges: readonly Edge[]
): Array<{ tasks: NormalizedTaskID[]; links: NormalizedLinkID[] }> {
  const verticesHash = new Map<NormalizedTaskID, VertexRec>();
  const stack: VertexRec[] = [];
  const edgesFromSource = groupBySource(edges);
  const components: Array<{ tasks: NormalizedTaskID[]; links: NormalizedLinkID[] }> = [];

  const getVertex = (id: NormalizedTaskID): VertexRec => {
    let v = verticesHash.get(id);
    if (!v) {
      v = { id, index: -1, lowLink: -1, onStack: false, visited: false, edge: undefined };
      verticesHash.set(id, v);
    }
    return v;
  };

  let index = 0;

  for (const startId of vertices) {
    const root = getVertex(startId);
    if (root.visited) continue;

    const workStack: VertexRec[] = [root];
    let recurse = false;

    while (workStack.length > 0) {
      const v = workStack.pop() as VertexRec;

      if (!v.visited) {
        v.index = index;
        v.lowLink = index;
        index++;
        stack.push(v);
        v.onStack = true;
        v.visited = true;
      }

      recurse = false;
      const outgoing = edgesFromSource.get(v.id) || [];
      for (let e = 0; e < outgoing.length; e++) {
        const w = getVertex(outgoing[e].target);
        w.edge = outgoing[e];
        if (w.index === -1) {
          // Not yet discovered — push self back then descend.
          workStack.push(v);
          workStack.push(w);
          recurse = true;
          break;
        } else if (w.onStack) {
          v.lowLink = Math.min(v.lowLink, w.index);
        }
      }
      if (recurse) continue;

      // Root of an SCC?
      if (v.index === v.lowLink) {
        const com: { tasks: NormalizedTaskID[]; links: NormalizedLinkID[] } = { tasks: [], links: [] };
        while (true) {
          const w = stack.pop() as VertexRec;
          w.onStack = false;
          com.tasks.push(w.id);
          if (w.edge) com.links.push(w.edge.id);
          if (w === v) break;
        }
        components.push(com);
      }

      // Propagate lowLink up the work stack (iterative recursion finalization).
      if (workStack.length > 0) {
        const parent = workStack[workStack.length - 1];
        parent.lowLink = Math.min(parent.lowLink, v.lowLink);
      }
    }
  }

  return components;
}

function groupBySource(edges: readonly Edge[]): Map<NormalizedTaskID, Edge[]> {
  const m = new Map<NormalizedTaskID, Edge[]>();
  for (const e of edges) {
    let arr = m.get(e.source);
    if (!arr) {
      arr = [];
      m.set(e.source, arr);
    }
    arr.push(e);
  }
  return m;
}

/**
 * Cycle detection — returns the list of cycles in the graph.
 * Matches v1 `findLoops()`:
 *  - self-loop edges (source === target) are reported as 1-task cycles
 *  - multi-vertex SCCs (cycles through ≥2 distinct tasks) are reported
 *  - size-1 SCCs without a self-loop are NOT reported (they're acyclic
 *    singletons, not cycles)
 */
export function findCycles_unmemoized(
  vertices: Iterable<NormalizedTaskID>,
  edges: readonly Edge[]
): Cycle[] {
  const cycles: Cycle[] = [];

  // Self-loops.
  for (const e of edges) {
    if (e.source === e.target) {
      cycles.push({ tasks: [e.source], links: [e.id] });
    }
  }

  const sccs = tarjanSCC(vertices, edges);
  for (const c of sccs) {
    if (c.tasks.length > 1) {
      cycles.push({ tasks: c.tasks.slice(), links: c.links.slice() });
    }
  }

  return cycles;
}

/**
 * Connected components over the UNDIRECTED projection of the directed graph.
 *
 * Returns one `ConnectedGroup` per maximal undirected component, each with
 * the task IDs and the link IDs that connect them. Disconnected singletons
 * (tasks with no edges) form their own group of size 1.
 *
 * Drives `gantt.getConnectedGroup` via the analysis pipeline.
 */
export function connectedComponents_unmemoized(
  vertices: Iterable<NormalizedTaskID>,
  edges: readonly Edge[]
): ConnectedGroup[] {
  const adj = new Map<NormalizedTaskID, Array<{ neighbor: NormalizedTaskID; linkId: NormalizedLinkID }>>();
  const ensure = (id: NormalizedTaskID): Array<{ neighbor: NormalizedTaskID; linkId: NormalizedLinkID }> => {
    let a = adj.get(id);
    if (!a) { a = []; adj.set(id, a); }
    return a;
  };
  for (const id of vertices) ensure(id);
  for (const e of edges) {
    ensure(e.source).push({ neighbor: e.target, linkId: e.id });
    ensure(e.target).push({ neighbor: e.source, linkId: e.id });
  }

  const visited = new Set<NormalizedTaskID>();
  const components: ConnectedGroup[] = [];

  for (const startId of adj.keys()) {
    if (visited.has(startId)) continue;
    const tasks: NormalizedTaskID[] = [];
    const linkSet = new Set<NormalizedLinkID>();
    const stack: NormalizedTaskID[] = [startId];
    while (stack.length > 0) {
      const id = stack.pop() as NormalizedTaskID;
      if (visited.has(id)) continue;
      visited.add(id);
      tasks.push(id);
      const nbrs = adj.get(id) ?? [];
      for (const { neighbor, linkId } of nbrs) {
        linkSet.add(linkId);
        if (!visited.has(neighbor)) stack.push(neighbor);
      }
    }
    components.push({ tasks, links: Array.from(linkSet) });
  }

  return components;
}

/**
 * `findCycleWithCandidate(baseEdges, candidateId, candidateEdges)` — returns
 * the cycle that adding `candidateEdges` would introduce, or `null` if the
 * resulting graph stays acyclic through them. Pure: `baseEdges` is not
 * mutated.
 *
 * One candidate link can contribute several edges: a link to/from a derived
 * summary expands to one edge per leaf descendant, and they all carry the
 * candidate's link id (see `core/api.ts:findCycleIfAdded`, which composes this
 * with the analysis graph to back `gantt.isCircularLink`).
 *
 * Engine ignores link direction labels here; only source→target matters for
 * cycle detection.
 */
export function findCycleWithCandidate(
  baseEdges: readonly Edge[],
  candidateId: NormalizedLinkID,
  candidateEdges: readonly Edge[]
): Cycle | null {
  const extended: Edge[] = baseEdges.slice();
  for (const e of candidateEdges) {
    if (e.source === e.target) {
      return { tasks: [e.source], links: [candidateId] };
    }
    extended.push(e);
  }

  const vertices = new Set<NormalizedTaskID>();
  for (const e of extended) {
    vertices.add(e.source);
    vertices.add(e.target);
  }

  const sccs = tarjanSCC(vertices, extended);
  for (const c of sccs) {
    if (c.tasks.length > 1 && c.links.indexOf(candidateId) !== -1) {
      // The new candidate participates in this cycle.
      return { tasks: c.tasks.slice(), links: c.links.slice() };
    }
  }

  return null;
}
